import React, { useMemo } from 'react';
import StatusTimelineCard from '../../Events/widgets/StatusTimelineCard';
import { getEventModerationRecords } from '../../Events/eventModerationRecords';
import { parseTimelineDate } from '../../Events/widgets/timelineDateUtils';
import { isAttendanceFinalized } from './attendanceFinalizeUtils';

const TIMELINE_TEMPLATE = [
  { id: 'event_submitted', title: 'Event Submitted' },
  { id: 'under_review', title: 'Under Review' },
  { id: 'changes_requested', title: 'Changes Requested' },
  { id: 'resubmit_review', title: 'Resubmit & Review' },
  { id: 'approval_publishing', title: 'Approval & Publishing' },
  { id: 'completed', title: 'Completed' },
];

const COMPLETED_STEP_ID = 'completed';

const REQUESTED_STATUSES = new Set(['requested', 'rejected', 'changes_requested']);

/** Which step is in progress for each latest moderation status. */
const ACTIVE_STEP_BY_STATUS = {
  pending: 'under_review',
  submitted: 'approval_publishing',
  requested: 'resubmit_review',
  rejected: 'resubmit_review',
  approved: 'completed',
  published: 'completed',
};

const FLOW_WITH_CHANGES = [
  'event_submitted',
  'under_review',
  'changes_requested',
  'resubmit_review',
  'approval_publishing',
];

const FLOW_WITHOUT_CHANGES = ['event_submitted', 'under_review', 'approval_publishing'];

const CHANGE_STEP_IDS = new Set(['changes_requested', 'resubmit_review']);

const APPROVED_STATUSES = new Set(['approved', 'published']);

/** Hide change steps only on a direct approve path (no requested in current window). */
const shouldHideChangeSteps = (latestStatus, window) =>
  APPROVED_STATUSES.has(latestStatus) &&
  !window.some((entry) => REQUESTED_STATUSES.has(entry._status));

export const normalizeModerationStatus = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const getModerationTimestamp = (entry) =>
  parseTimelineDate(entry?.requestedAt) ||
  parseTimelineDate(entry?.updatedAt) ||
  parseTimelineDate(entry?.createdAt);

export const toSortedModerationHistory = (event) => {
  const records = getEventModerationRecords(event);
  if (!records.length) return [];

  return [...records]
    .map((item, index) => ({
      ...item,
      _status: normalizeModerationStatus(item?.status),
      _at: getModerationTimestamp(item),
      _apiIndex: index,
    }))
    .sort((a, b) => {
      const timeDiff = (a._at?.getTime?.() ?? 0) - (b._at?.getTime?.() ?? 0);
      if (timeDiff !== 0) return timeDiff;
      return a._apiIndex - b._apiIndex;
    });
};

/** From last `pending` in history, else last `requested`, through latest. */
export const getModerationTimelineWindow = (history) => {
  if (!history.length) return [];

  let anchorIndex = -1;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]._status === 'pending') {
      anchorIndex = index;
      break;
    }
  }

  if (anchorIndex === -1) {
    for (let index = history.length - 1; index >= 0; index -= 1) {
      if (REQUESTED_STATUSES.has(history[index]._status)) {
        anchorIndex = index;
        break;
      }
    }
  }

  if (anchorIndex === -1) return history;
  return history.slice(anchorIndex);
};

const getStepIndex = (stepId) => TIMELINE_TEMPLATE.findIndex((step) => step.id === stepId);

const getStepState = (stepId, latestStatus, flowIds, activeStepId, isFinalized) => {
  if (stepId === COMPLETED_STEP_ID) {
    if (isFinalized) return 'completed';
    if (activeStepId === COMPLETED_STEP_ID) return 'in_progress';
    return 'pending';
  }

  if (latestStatus === 'completed') {
    return flowIds.includes(stepId) ? 'completed' : 'pending';
  }

  if (!flowIds.includes(stepId)) return 'pending';
  if (!activeStepId) return 'pending';

  const stepIndex = getStepIndex(stepId);
  const activeIndex = getStepIndex(activeStepId);

  if (stepId === activeStepId) return 'in_progress';
  if (stepIndex < activeIndex) return 'completed';
  return 'pending';
};

/** Only return a timestamp when the current window has a clear moment for that step. */
const getCompletedStepTimestamps = (window, event, { hasChangeStepsInWindow, isFinalized }) => {
  const requestedEntry = hasChangeStepsInWindow
    ? [...window].reverse().find((entry) => REQUESTED_STATUSES.has(entry._status))
    : null;
  const requestedAtMs = requestedEntry?._at?.getTime?.() ?? null;

  const resubmitEntry =
    requestedAtMs != null
      ? [...window]
          .reverse()
          .find(
            (entry) =>
              entry._status === 'submitted' && (entry._at?.getTime?.() ?? 0) >= requestedAtMs,
          )
      : null;

  const approvedEntry = [...window].reverse().find((entry) => entry._status === 'approved');

  let underReviewAt = window.find((entry) => entry._status === 'pending')?._at ?? null;
  if (hasChangeStepsInWindow && requestedEntry) {
    const requestedIndex = window.findIndex((entry) => entry === requestedEntry);
    for (let index = requestedIndex - 1; index >= 0; index -= 1) {
      if (window[index]._status === 'pending') {
        underReviewAt = window[index]._at;
        break;
      }
    }
  }

  const eventSubmittedAt =
    parseTimelineDate(event?.submittedAt) || parseTimelineDate(event?.createdAt) || null;

  return {
    event_submitted: eventSubmittedAt,
    under_review: underReviewAt,
    changes_requested: requestedEntry?._at ?? null,
    resubmit_review: resubmitEntry?._at ?? null,
    approval_publishing: approvedEntry?._at ?? null,
    completed: isFinalized ? parseTimelineDate(event?.updatedAt) : null,
  };
};

/** Derives timeline steps from moderation window (anchor → latest). */
export const deriveEventModerationTimeline = (event, { showCompletedTimeline = true } = {}) => {
  const emptySteps = TIMELINE_TEMPLATE.map((step) => ({
    ...step,
    state: 'pending',
    timestamp: null,
  }));

  const history = toSortedModerationHistory(event);
  if (!history.length) {
    return showCompletedTimeline
      ? emptySteps
      : emptySteps.filter((step) => step.id !== COMPLETED_STEP_ID);
  }

  const window = getModerationTimelineWindow(history);
  const latestStatus = window[window.length - 1]._status;
  const hasChangeStepsInWindow = window.some((entry) => REQUESTED_STATUSES.has(entry._status));
  const hideChangeSteps = shouldHideChangeSteps(latestStatus, window);
  // Visibility (hide) vs progression (flow): show change steps in most cases, but only
  // mark them completed when the current window actually includes a change request.
  const flowIds = hasChangeStepsInWindow ? FLOW_WITH_CHANGES : FLOW_WITHOUT_CHANGES;
  const activeStepId = ACTIVE_STEP_BY_STATUS[latestStatus] ?? null;

  const isFinalized = isAttendanceFinalized(event);

  const timestampsByStep = getCompletedStepTimestamps(window, event, {
    hasChangeStepsInWindow,
    isFinalized,
  });

  const steps = TIMELINE_TEMPLATE.filter((step) => {
    if (CHANGE_STEP_IDS.has(step.id) && hideChangeSteps) return false;
    if (step.id === COMPLETED_STEP_ID && !showCompletedTimeline) return false;
    return true;
  }).map((step) => {
    const state = getStepState(step.id, latestStatus, flowIds, activeStepId, isFinalized);
    const timestamp = state === 'completed' ? (timestampsByStep[step.id] ?? null) : null;

    return { ...step, state, timestamp };
  });

  return steps;
};

export const getLatestModerationStatus = (event) => {
  const history = toSortedModerationHistory(event);
  if (history.length) return history[history.length - 1]._status;
  return normalizeModerationStatus(getEventModerationRecords(event)[0]?.status);
};

const EventModerationTimeline = ({
  event,
  title = 'Status Timeline',
  showCompletedTimeline = true,
  containerSx,
}) => {
  const steps = useMemo(
    () => deriveEventModerationTimeline(event, { showCompletedTimeline }),
    [event, showCompletedTimeline],
  );

  return <StatusTimelineCard title={title} steps={steps} containerSx={containerSx} />;
};

export default EventModerationTimeline;
